/**
 * FamilyService -- Manages family profile system: members, invites, co-admins.
 *
 * All data flows through the AWENI PHP gateway API at /aweni/api/v1/family/*.
 * Admin creates members, generates invite links, manages roles.
 * Children are linked to admin's account with inherited settings + parental controls.
 */

import type { ApiClient, Lang } from '../types';

// ---------- Interfaces ----------

export interface FamilyMember {
  profileId: number;
  name: string;
  avatar?: string;
  role: 'admin' | 'co-admin' | 'member' | 'child';
  email?: string;
  linkedToAdmin: number;
  status: 'active' | 'pending' | 'suspended';
  createdAt: string;
  lastActive?: string;
  enabledModules?: string[];
  parentalControlsEnabled?: boolean;
  age?: number;
}

export interface InviteLink {
  code: string;
  type: 'member' | 'child' | 'co-admin';
  createdBy: number;
  expiresAt: string;
  maxUses: number;
  usedCount: number;
  settings?: {
    enabledModules: string[];
    parentalControls?: boolean;
  };
  familyName?: string;
  adminName?: string;
}

export interface FamilyOverview {
  familyName: string;
  adminId: number;
  members: FamilyMember[];
  pendingInvites: InviteLink[];
}

export interface MemberActivity {
  date: string;
  minutesUsed: number;
  gamesPlayed: number;
  cardsReviewed: number;
  articlesRead: number;
}

export interface DailyReport {
  date: string;
  members: Array<{
    profileId: number;
    name: string;
    activity: MemberActivity;
    alerts: string[];
  }>;
}

export interface CreateMemberPayload {
  name: string;
  role: 'member' | 'child' | 'co-admin';
  email?: string;
  age?: number;
  enabledModules: string[];
  parentalControls: boolean;
}

export interface CreateInvitePayload {
  type: 'member' | 'child' | 'co-admin';
  maxUses?: number;
  enabledModules?: string[];
  parentalControls?: boolean;
}

export interface AcceptInvitePayload {
  name: string;
  password?: string;
  email?: string;
}

// ---------- Service ----------

let _instance: FamilyService | null = null;

export class FamilyService {
  private api: ApiClient;
  private baseUrl = '/aweni/api/v1/family';

  constructor(api: ApiClient) {
    this.api = api;
  }

  static init(api: ApiClient): FamilyService {
    _instance = new FamilyService(api);
    return _instance;
  }

  static getInstance(): FamilyService | null {
    return _instance;
  }

  // ---- Family overview ----

  async getFamily(): Promise<FamilyOverview> {
    return this.api.get<FamilyOverview>(this.baseUrl);
  }

  // ---- Member management ----

  async createMember(payload: CreateMemberPayload): Promise<FamilyMember> {
    return this.api.post<FamilyMember>(`${this.baseUrl}/members`, payload);
  }

  async updateMember(memberId: number, updates: Partial<Pick<FamilyMember, 'name' | 'role' | 'status' | 'enabledModules' | 'parentalControlsEnabled'>>): Promise<FamilyMember> {
    return this.api.put<FamilyMember>(`${this.baseUrl}/members/${memberId}`, updates);
  }

  async removeMember(memberId: number): Promise<void> {
    await this.api.delete(`${this.baseUrl}/members/${memberId}`);
  }

  async promoteToCoadmin(memberId: number): Promise<FamilyMember> {
    return this.api.put<FamilyMember>(`${this.baseUrl}/co-admin/${memberId}`, { promote: true });
  }

  async demoteFromCoadmin(memberId: number): Promise<FamilyMember> {
    return this.api.put<FamilyMember>(`${this.baseUrl}/co-admin/${memberId}`, { promote: false });
  }

  async suspendMember(memberId: number): Promise<FamilyMember> {
    return this.updateMember(memberId, { status: 'suspended' });
  }

  async reactivateMember(memberId: number): Promise<FamilyMember> {
    return this.updateMember(memberId, { status: 'active' });
  }

  // ---- Invites ----

  async createInvite(payload: CreateInvitePayload): Promise<InviteLink> {
    return this.api.post<InviteLink>(`${this.baseUrl}/invite`, payload);
  }

  async validateInvite(code: string): Promise<InviteLink & { valid: boolean; error?: string }> {
    return this.api.get(`${this.baseUrl}/invite/${code}`);
  }

  async acceptInvite(code: string, payload: AcceptInvitePayload): Promise<{ success: boolean; profileId: number; familyName: string }> {
    return this.api.post(`${this.baseUrl}/invite/${code}/accept`, payload);
  }

  async revokeInvite(code: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/invite/${code}`);
  }

  // ---- Activity reports ----

  async getDailyReport(): Promise<DailyReport> {
    return this.api.get<DailyReport>(`${this.baseUrl}/reports/daily`);
  }

  async getMemberActivity(memberId: number, days: number = 7): Promise<MemberActivity[]> {
    return this.api.get<MemberActivity[]>(`${this.baseUrl}/members/${memberId}/activity?days=${days}`);
  }

  // ---- Helpers ----

  generateInviteUrl(code: string): string {
    return `${window.location.origin}/aweni/join/${code}`;
  }

  generateWhatsAppShareUrl(code: string, lang: Lang = 'en'): string {
    const url = this.generateInviteUrl(code);
    const messages: Record<Lang, string> = {
      en: `Join my family on AWENI! Tap here to install and join: ${url}`,
      fr: `Rejoins ma famille sur AWENI ! Appuie ici pour installer et rejoindre : ${url}`,
      ar: `\u0627\u0646\u0636\u0645 \u0625\u0644\u0649 \u0639\u0627\u0626\u0644\u062a\u064a \u0639\u0644\u0649 AWENI! \u0627\u0636\u063a\u0637 \u0647\u0646\u0627 \u0644\u0644\u062a\u062b\u0628\u064a\u062a \u0648\u0627\u0644\u0627\u0646\u0636\u0645\u0627\u0645: ${url}`,
      es: `\u00danete a mi familia en AWENI. Toca aqu\u00ed para instalar y unirte: ${url}`,
      de: `Tritt meiner Familie auf AWENI bei! Tippe hier zum Installieren und Beitreten: ${url}`,
    };
    return `https://wa.me/?text=${encodeURIComponent(messages[lang] || messages.en)}`;
  }

  generateEmailShareUrl(code: string, lang: Lang = 'en'): string {
    const url = this.generateInviteUrl(code);
    const subjects: Record<Lang, string> = {
      en: 'Join my family on AWENI',
      fr: 'Rejoins ma famille sur AWENI',
      ar: '\u0627\u0646\u0636\u0645 \u0625\u0644\u0649 \u0639\u0627\u0626\u0644\u062a\u064a \u0639\u0644\u0649 AWENI',
      es: '\u00danete a mi familia en AWENI',
      de: 'Tritt meiner Familie auf AWENI bei',
    };
    const bodies: Record<Lang, string> = {
      en: `Hi!\n\nI'd like you to join my family on AWENI, the personal assistant app for learning and life.\n\nClick this link to join: ${url}\n\nSee you there!`,
      fr: `Salut !\n\nJe t'invite \u00e0 rejoindre ma famille sur AWENI.\n\nClique ici pour rejoindre : ${url}\n\n\u00c0 bient\u00f4t !`,
      ar: `\u0645\u0631\u062d\u0628\u0627!\n\n\u0623\u062f\u0639\u0648\u0643 \u0644\u0644\u0627\u0646\u0636\u0645\u0627\u0645 \u0625\u0644\u0649 \u0639\u0627\u0626\u0644\u062a\u064a \u0639\u0644\u0649 AWENI.\n\n\u0627\u0636\u063a\u0637 \u0647\u0646\u0627 \u0644\u0644\u0627\u0646\u0636\u0645\u0627\u0645: ${url}`,
      es: `\u00a1Hola!\n\nTe invito a unirte a mi familia en AWENI.\n\nHaz clic aqu\u00ed: ${url}\n\n\u00a1Nos vemos!`,
      de: `Hallo!\n\nIch lade dich ein, meiner Familie auf AWENI beizutreten.\n\nKlicke hier: ${url}\n\nBis bald!`,
    };
    const subject = encodeURIComponent(subjects[lang] || subjects.en);
    const body = encodeURIComponent(bodies[lang] || bodies.en);
    return `mailto:?subject=${subject}&body=${body}`;
  }

  /**
   * Generate a simple inline SVG QR code for the invite link.
   * Uses a basic QR-like pattern (placeholder -- real QR needs a library).
   * In production, the API returns a pre-rendered QR SVG.
   */
  async getInviteQR(code: string): Promise<string> {
    try {
      const result = await this.api.get<{ svg: string }>(`${this.baseUrl}/invite/${code}/qr`);
      return result.svg;
    } catch {
      // Fallback: render code as text in a box
      return `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
        <rect width="200" height="200" rx="12" fill="#f0fdf4" stroke="#0A0A0A" stroke-width="2"/>
        <text x="100" y="90" text-anchor="middle" font-size="14" font-family="monospace" fill="#1a1a2e">AWENI</text>
        <text x="100" y="115" text-anchor="middle" font-size="11" font-family="monospace" fill="#64748b">${code}</text>
        <text x="100" y="140" text-anchor="middle" font-size="9" font-family="sans-serif" fill="#94a3b8">Scan or use code above</text>
      </svg>`;
    }
  }

  // ---- Role checks ----

  isAdmin(member: FamilyMember): boolean {
    return member.role === 'admin';
  }

  isCoAdmin(member: FamilyMember): boolean {
    return member.role === 'co-admin';
  }

  canManageMembers(member: FamilyMember): boolean {
    return member.role === 'admin' || member.role === 'co-admin';
  }

  canRemoveAdmin(actor: FamilyMember, target: FamilyMember): boolean {
    // Only the primary admin can remove themselves; co-admins cannot remove admin
    return actor.role === 'admin' && target.role !== 'admin';
  }

  // ---- Default modules ----

  getDefaultEnabledModules(): string[] {
    return ['home', 'games', 'memoriz', 'blog', 'community'];
  }

  getAllModules(): string[] {
    return ['home', 'games', 'memoriz', 'balancetab', 'selecto', 'blog', 'chat', 'community'];
  }
}

export function getFamilyService(): FamilyService | null {
  return FamilyService.getInstance();
}
